import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Input, Table, Tag, Typography, App } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import TimeRangeChip, {
  resolvePartitionRangeDayjs,
  type TimeRangeValue,
} from '@components/TimeRangeChip';
import { JobStatusTag } from '@components/StatusTag';
import {
  getSceneGenerationJobs,
  type JobItem,
} from '@/apis/simulation';

const { Text } = Typography;

const formatTime = (val: string | null | undefined) =>
  val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-';

interface Props {
  taskTypeName: 'SCENE' | 'SCENE_PE';
  title?: string;
  /** 父页面在入库完成时递增，用来刷新这一份列表。 */
  refreshToken?: number;
}

/** 场景生成任务表。规划页和感知页各挂一份，靠 taskTypeName 分开查。 */
const SceneGenJobList: React.FC<Props> = ({
  taskTypeName,
  title = '场景生成任务',
  refreshToken = 0,
}) => {
  const { message } = App.useApp();
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobPagination, setJobPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [jobFilters, setJobFilters] = useState<{
    apiCode?: string;
    sceneName?: string;
    creator?: string;
    createTimeRange?: TimeRangeValue | null;
  }>({ createTimeRange: { mode: 'preset', days: 30 } });

  const fetchJobs = useCallback(async (current = 1, pageSize = 20) => {
    setJobLoading(true);
    try {
      const range = resolvePartitionRangeDayjs(jobFilters.createTimeRange ?? null);
      const resp = await getSceneGenerationJobs({
        current, pageSize,
        taskTypeName,
        apiCode: jobFilters.apiCode?.trim() || undefined,
        sceneName: jobFilters.sceneName?.trim() || undefined,
        creator: jobFilters.creator?.trim() || undefined,
        createTimeStart: range?.start ? range.start.toISOString() : undefined,
        createTimeEnd: range?.end ? range.end.toISOString() : undefined,
      });
      setJobs(resp.tasks);
      setJobPagination({
        current: resp.page?.current ?? current,
        pageSize: resp.page?.pageSize ?? pageSize,
        total: resp.page?.totalElements ?? resp.total,
      });
    } finally {
      setJobLoading(false);
    }
  }, [jobFilters, taskTypeName]);

  useEffect(() => {
    fetchJobs(1, jobPagination.pageSize);
  // 只在父页面要求刷新时重拉。筛选条件由「查询」按钮读取当前闭包，避免输入过程中反复请求。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const jobColumns: ColumnsType<JobItem> = [
    {
      title: 'Job ID',
      dataIndex: 'jobId',
      width: 100,
      render: (id: number) => (
        <span
          title="双击复制"
          style={{ cursor: 'copy' }}
          onDoubleClick={async () => {
            try {
              await navigator.clipboard.writeText(String(id));
              message.success('已复制 Job ID');
            } catch {
              message.error('复制失败');
            }
          }}
        >
          {id}
        </span>
      ),
    },
    {
      title: 'Job 名称', dataIndex: 'jobName', width: 220,
      render: (text: string, record: JobItem) => (
        <Link to={`/scene-generation/job-tasks/${record.jobId}`}
          style={{ color: '#5e6ad2', cursor: 'pointer' }}>
          {text || '-'}
        </Link>
      ),
    },
    {
      title: '状态', dataIndex: 'status', width: 110,
      render: (_: number, record: JobItem) => (
        <JobStatusTag status={record.status} statusName={record.statusName} />
      ),
    },
    { title: '总任务数', dataIndex: 'totalTaskNum', width: 90 },
    { title: '成功', dataIndex: 'successTaskNum', width: 70 },
    { title: '失败', dataIndex: 'failTaskNum', width: 70 },
    {
      title: '加入场景集合', dataIndex: 'addToSceneSetName', width: 160,
      render: (_: string | null | undefined, record: JobItem) => {
        const name = record.addToSceneSetName;
        if (!name && !record.addToSceneSetNodeId) {
          return <Text type="secondary">-</Text>;
        }
        return <Tag color="blue">{name || `#${record.addToSceneSetNodeId}`}</Tag>;
      },
    },
    { title: '创建人', dataIndex: 'creator', width: 100 },
    { title: '创建时间', dataIndex: 'createTime', width: 180, render: formatTime },
    { title: '结束时间', dataIndex: 'endTime', width: 180, render: formatTime },
  ];

  return (
    <Card style={{ marginTop: 16 }} title={title}>
      <div style={{
        marginBottom: 12,
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
      }}>
        <Input
          placeholder="api_code(模糊)"
          value={jobFilters.apiCode}
          allowClear
          style={{ width: 180 }}
          onChange={e => setJobFilters(prev => ({ ...prev, apiCode: e.target.value }))}
          onPressEnter={() => fetchJobs(1, jobPagination.pageSize)}
        />
        <Input
          placeholder="场景名称(模糊)"
          value={jobFilters.sceneName}
          allowClear
          style={{ width: 180 }}
          onChange={e => setJobFilters(prev => ({ ...prev, sceneName: e.target.value }))}
          onPressEnter={() => fetchJobs(1, jobPagination.pageSize)}
        />
        <Input
          placeholder="创建人(前缀匹配)"
          value={jobFilters.creator}
          allowClear
          style={{ width: 180 }}
          onChange={e => setJobFilters(prev => ({ ...prev, creator: e.target.value }))}
          onPressEnter={() => fetchJobs(1, jobPagination.pageSize)}
        />
        <TimeRangeChip
          value={jobFilters.createTimeRange ?? null}
          onChange={val => setJobFilters(prev => ({ ...prev, createTimeRange: val }))}
        />
        <Button type="primary" onClick={() => fetchJobs(1, jobPagination.pageSize)}>
          查询
        </Button>
        <Button
          onClick={() => {
            setJobFilters({ createTimeRange: { mode: 'preset', days: 30 } });
            setTimeout(() => fetchJobs(1, jobPagination.pageSize), 0);
          }}
        >
          重置
        </Button>
      </div>
      <Table<JobItem>
        rowKey="jobId"
        columns={jobColumns}
        dataSource={jobs}
        loading={jobLoading}
        pagination={{
          current: jobPagination.current,
          pageSize: jobPagination.pageSize,
          total: jobPagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100', '1000'],
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, size) => fetchJobs(page, size),
        }}
      />
    </Card>
  );
};

export default SceneGenJobList;
